/**
 * EAUG-77 order modification: the JSON of the /api/order-edit endpoints.
 * Mirror of backend/src/order-edit/dto/order-edit.types.ts, which is the
 * source: keep the two identical.
 */

export type EditCapability = 'full' | 'drinks_add';
export type EditCategory = 'meal' | 'drink' | 'option';
export type MenusLock = 'dishes_chosen' | 'capacity_unknown';
export type LineLock =
  | 'dishes_chosen'
  | 'capacity_unknown'
  | 'not_offered'
  | 'decrease_only'
  | 'drinks_add_only'
  | 'price_unavailable'
  | 'fixed';

export interface EditLine {
  product: string;
  label: string;
  category: EditCategory;
  photo: string | null;
  unitCents: number | null;
  current: number;
  min: number;
  max: number | null;
  lock: LineLock | null;
  /**
   * null: offered at the current headcount. N: offered only once the draft's
   * menus total N (PR 1: spec §5.3/§5.4 evaluate offers at H'). Kept at 0 below N.
   */
  offeredFrom: number | null;
}

export interface EditView {
  capability: EditCapability;
  firstName: string;
  /** YYYY-MM-DD */
  date: string;
  /** 'La Salle' | 'La Roue' | 'La Salle et La Roue' | 'La Serre' | '' */
  roomLabel: string;
  /** ISO instant */
  deadline: string;
  /** 'mercredi 09/12 à 18h00' */
  deadlineLabel: string;
  headcount: number;
  capacity: number | null;
  menusLock: MenusLock | null;
  /** drinks_add: menus = [] and options = [] */
  sections: { menus: EditLine[]; drinks: EditLine[]; options: EditLine[] };
  maskedEmail: string | null;
}

export interface Target {
  product: string;
  quantity: number;
}

export interface QuoteLine {
  product: string;
  label: string;
  from: number;
  to: number;
  unitCents: number;
  vat: string;
}

export interface Quote {
  lines: QuoteLine[];
  netCents: number;
  headcount: number;
}

export type SubmitResult =
  | { status: 'payment_required'; modificationId: number; checkoutUrl: string }
  | { status: 'applied'; modificationId: number }
  | { status: 'processing'; modificationId: number };

export type ModificationStatus = 'pending_payment' | 'paid' | 'applied' | 'expired' | 'needs_attention';

export const RULE_CODES = [
  'line_not_editable',
  'below_minimum',
  'above_maximum',
  'headcount_decrease',
  'above_capacity',
  'menus_locked',
  'family_switch',
  'brunch_invalid',
  'not_offered',
  'no_change',
] as const;
export type RuleCode = (typeof RULE_CODES)[number];

/** Every `error` code the endpoints answer, plus two the client derives. */
export type EditErrorCode =
  | 'invalid_link'
  | 'disabled'
  | 'order_edit_unavailable'
  | 'deadline_passed'
  | 'not_eligible'
  | 'price_unavailable'
  | RuleCode
  | 'quote_changed'
  | 'payment_in_progress'
  | 'modification_pending'
  | 'headcount_changed'
  | 'too_many_requests'
  /** No HTTP answer at all (offline, timeout). */
  | 'network'
  /** An answer without a known code. */
  | 'unknown';

/** Exactly one credential per request (spec §4.2). */
export type EditCredential = { token: string } | { menuToken: string };
